import { lazy, Suspense } from 'react';

import styled from 'styled-components';

import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { MessageFeedback } from 'api/ai-builder/requests';

import { MessageActions } from './message-actions';

const MarkdownText = lazy(() => import('@components/markdown-text'));

type AssistantMessageProps = {
  text: string;
  errorMessage?: string;
  feedbackRating?: MessageFeedback | null;
  onFeedback?: (feedbackRating: MessageFeedback | null) => void;
  onSubmitDislikeFeedback?: (reasons: string[], details: string) => void;
  isStreaming?: boolean;
};

export const AssistantMessage = ({
  text,
  errorMessage,
  feedbackRating,
  onFeedback,
  onSubmitDislikeFeedback,
  isStreaming,
}: AssistantMessageProps) => {
  if (errorMessage) {
    return (
      <StatusMessageContainer>
        <Stack orientation="horizontal" space={2}>
          <IconFormIndicatorWarning />
          <Text color="elementOrange200" font="regular14" text={text} />
        </Stack>
      </StatusMessageContainer>
    );
  }

  return (
    <div>
      <AssistantBorder>
        <AssistantMessageContainer>
          <Suspense fallback={<Text color="textPrimary" font="regular14" text={text} />}>
            <MarkdownText text={text} />
          </Suspense>
        </AssistantMessageContainer>
      </AssistantBorder>
      {!isStreaming && onFeedback && (
        <MessageActions
          text={text}
          feedbackRating={feedbackRating}
          onFeedback={onFeedback}
          onSubmitDislikeFeedback={onSubmitDislikeFeedback}
        />
      )}
    </div>
  );
};

const AssistantMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const StatusMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: theme.color.orangeTrans10,
});

const AssistantBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: theme.gradient.aiSurface30,
});
